import {forwardRef, type RefObject} from 'react';
import {useCallback, useImperativeHandle, useRef} from 'react';
import type {TextInput} from 'react-native';

import type {Nullable, WithDomStyleProp} from '../../utils/utility-types';
import type {Ref, Props as TextInputProps} from '../TextInput/types';
import type {Props} from './types';

import {useController} from './controller';
import {
  Container,
  HiddenInput,
  InputsContainer,
  InputText,
  InputView,
} from './styles';

const nativeProps: TextInputProps['nativeProps'] = {
  blurOnSubmit: false,
  caretHidden: true,
  contextMenuHidden: true,
  selectionColor: 'transparent',
  textContentType: 'oneTimeCode',
  underlineColorAndroid: 'transparent',
};

const CodeInput = forwardRef<Ref, WithDomStyleProp<Props>>(
  ({disabled, length = 6, size = 50, spacing, testID, ...rest}, ref) => {
    const {blurHandler, changeHandler, code, focusHandler, isFocused} =
      useController(rest);

    const input = useRef<Ref>(null);

    useImperativeHandle(ref, () => ({
      blur: () => input.current?.blur(),
      clear: () => input.current?.clear(),
      focus: () => input.current?.focus(),
      ref: {
        native: input as unknown as RefObject<Nullable<TextInput>>,
        web: input as unknown as RefObject<Nullable<HTMLInputElement>>,
      },
      scrollTo: (...args) => input.current?.scrollTo(...args),
      select: () => {
        /* do nothing */
      },
    }));

    const onClick = useCallback(() => {
      input.current?.focus();
    }, []);

    return (
      <Container onClick={onClick} testID={testID}>
        <HiddenInput
          autoFocus
          disabled={disabled}
          inputMode="numeric"
          maxLength={length}
          nativeProps={nativeProps}
          onBlur={blurHandler}
          onChange={changeHandler}
          onFocus={focusHandler}
          ref={input}
          size={size}
          value={code}
        />
        <InputsContainer disabled={disabled} size={size} spacing={spacing}>
          {[...Array(Math.abs(length || 6))].fill('').map((_, i) => (
            <InputView
              index={i}
              isActive={i === code.length}
              isFilled={!!code[i]}
              isFocused={isFocused}
              key={i}
              length={length}
              size={size}
              spacing={spacing}
            >
              <InputText size={size}>{code[i]}</InputText>
            </InputView>
          ))}
        </InputsContainer>
      </Container>
    );
  },
);

export {CodeInput};
